import {
  Divider,
  Select,
  Spin,
  Tooltip,
  Row,
  Col,
  Modal,
  Button,
  Card,
  Avatar,
  Form,
  Input,
  Upload,
  message,
} from 'antd'
import './Bots.css'
import { useEffect, useState, useRef } from 'react'
import { getBots, editBot } from 'src/api/user'
import { downloadImageById } from 'src/api/image'
import { EditOutlined, UploadOutlined, SmileOutlined } from '@ant-design/icons'
import EmojiPicker from 'emoji-picker-react'

const Bots = () => {
  const [botUsers, setBotUsers] = useState([])
  const [botUserId, setBotUserId] = useState(undefined)
  const [loading, setLoading] = useState(true)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [selectedBot, setSelectedBot] = useState(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [description, setDescription] = useState('')
  const [profileFileList, setProfileFileList] = useState([])
  const [coverFileList, setCoverFileList] = useState([])
  const bioTextAreaRef = useRef<HTMLTextAreaElement>(null)

  const handleEdit = (bot) => {
    setIsModalVisible(true)
    setSelectedBot(bot)
    form.setFieldsValue({
      username: bot.label,
      first_name: bot.first_name,
      bio: bot.bio,
      profile_photo: bot.avatar,
      cover_photo: bot.cover,
    })
    setDescription(bot.bio || '')
  }

  const fetchBotUser = async () => {
    setLoading(true)

    try {
      const response = await getBots()

      const usersList = response.data.map((element) => ({
        label: element.username,
        value: element.user_id,
        first_name: element.first_name,
        avatar: null,
        cover: null,
        bio: element.bio || '',
        profile_photo: element.profile_photo,
        cover_photo: element.cover_photo,
        avatarLoading: !!element.profile_photo,
        coverLoading: !!element.cover_photo,
      }))

      setBotUsers(usersList)
      setLoading(false)

      response.data.forEach(async (element) => {
        try {
          const avatar = element.profile_photo
            ? await downloadImageById(element.profile_photo)
            : null

          const cover = element.cover_photo
            ? await downloadImageById(element.cover_photo)
            : null

          setBotUsers((prev) =>
            prev.map((bot) =>
              bot.value === element.user_id
                ? {
                    ...bot,
                    avatar,
                    cover,
                    avatarLoading: false,
                    coverLoading: false,
                  }
                : bot
            )
          )
        } catch (err) {
          console.error('Image download failed', err)

          setBotUsers((prev) =>
            prev.map((bot) =>
              bot.value === element.user_id
                ? { ...bot, avatarLoading: false, coverLoading: false }
                : bot
            )
          )
        }
      })
    } catch (error) {
      message.error('Failed to load bots')
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    setBotUserId(e)
  }

  useEffect(() => {
    if (selectedBot) {
      setProfileFileList([
        {
          uid: '-1',
          name: 'profile.jpg',
          status: 'done',
          url: selectedBot.avatar,
        },
      ])
      setCoverFileList([
        {
          uid: '-2',
          name: 'cover.jpg',
          status: 'done',
          url: selectedBot.cover,
        },
      ])
      setDescription(selectedBot.bio || '')
    }
  }, [selectedBot])

  useEffect(() => {
    fetchBotUser()
  }, [])

  const handleEmojiSelect = (emojiObject) => {
    const textarea = document.getElementById(
      'editTextArea'
    ) as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    setDescription((prevContent) => {
      const textBefore = prevContent.slice(0, start)
      const textAfter = prevContent.slice(end)

      const updatedValue = textBefore + emojiObject.emoji + textAfter
      form.setFieldsValue({ bio: updatedValue })
      return textBefore + emojiObject.emoji + textAfter
    })
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + emojiObject.emoji.length
      textarea.focus()
    }, 0)
  }

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker)
  }

  return (
    <>
      <Spin spinning={loading}>
        <Select
          placeholder="Filter by Username"
          allowClear
          options={botUsers.map((bot) => ({
            label: bot.label,
            value: bot.value,
          }))}
          onChange={handleInputChange}
          value={botUserId}
          className="search-input"
          showSearch
          optionFilterProp="label"
          style={{ marginBottom: 20, width: 300 }}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
        <Divider />
        <div className="custom-card-wrapper">
          <Row gutter={[16, 16]}>
            {(botUserId
              ? botUsers.filter((bot) => bot.value === botUserId)
              : botUsers
            ).map((bot) => (
              <Col xs={18} sm={10} md={6} lg={4} key={bot.value}>
                <Card
                  hoverable
                  cover={
                    <div
                      style={{
                        height: 120,
                        position: 'relative',
                      }}
                    >
                      {bot.coverLoading && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: '#f0f2f5',
                          }}
                        >
                          <Spin size="small" />
                        </div>
                      )}

                      {bot.cover && (
                        <div
                          style={{
                            height: '100%',
                            background: `url(${bot.cover}) center / cover no-repeat`,
                          }}
                        />
                      )}

                      <div
                        style={{
                          position: 'absolute',
                          bottom: -32,
                          left: 16,
                        }}
                      >
                        {bot.avatarLoading ? (
                          <Avatar size={64}>
                            <Spin size="small" />
                          </Avatar>
                        ) : (
                          <Avatar
                            src={bot.avatar || '/default-avatar.png'}
                            size={64}
                            style={{
                              border: '2px solid white',
                              backgroundColor: '#fff',
                            }}
                          />
                        )}
                      </div>
                    </div>
                  }
                  style={{ marginTop: 32 }}
                  actions={[
                    <Tooltip title="Edit Bot">
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(bot)}
                      />
                    </Tooltip>,
                  ]}
                >
                  <Card.Meta title={bot.label} style={{ marginTop: 24 }} />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </Spin>
      <Modal
        title="Edit Bot"
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          setBotUserId(undefined)
        }}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Save
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            const { first_name, bio, profile_photo, cover_photo } = values

            const profileFile = profileFileList[0]?.originFileObj || null
            const coverFile = coverFileList[0]?.originFileObj || null

            setLoading(true)

            await editBot(
              selectedBot.value,
              selectedBot.label,
              first_name,
              bio,
              profileFile,
              coverFile
            )
            setIsModalVisible(false)
            fetchBotUser()
          }}
        >
          <Form.Item label="Username">
            <Input value={selectedBot?.label} disabled />
          </Form.Item>

          <Form.Item
            name="first_name"
            label="Header"
            rules={[{ required: true, message: 'Please input first name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="bio"
            label="Bio"
            rules={[{ required: true, message: 'Please input bot bio!' }]}
          >
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '5px',
                }}
              >
                <Button
                  type="text"
                  icon={<SmileOutlined />}
                  onClick={toggleEmojiPicker}
                  style={{
                    backgroundColor: '#ffe0bd',
                    color: 'black',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    padding: '5px',
                    marginBottom: '5px',
                  }}
                />
              </div>
              <Input.TextArea
                id="editTextArea"
                ref={bioTextAreaRef}
                rows={4}
                placeholder="Enter bot bio..."
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  form.setFieldsValue({ bio: e.target.value })
                }}
                style={{ marginBottom: '16px', paddingRight: '32px' }}
              />
              {showEmojiPicker && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 2,
                    right: 0,
                    bottom: '100%',
                  }}
                >
                  <EmojiPicker
                    onEmojiClick={handleEmojiSelect}
                    style={{ maxHeight: '360px', overflowY: 'auto' }}
                  />
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="profile_photo"
            label="Profile Photo"
            valuePropName="file"
            getValueFromEvent={(e) => e?.fileList?.[0] || null}
          >
            <Upload
              accept="image/*"
              className="custom-upload"
              listType="picture-card"
              maxCount={1}
              fileList={profileFileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setProfileFileList(fileList)}
              showUploadList={{ showPreviewIcon: false }}
            >
              {profileFileList.length >= 1 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="cover_photo"
            label="Cover Photo"
            valuePropName="file"
            getValueFromEvent={(e) => e?.fileList?.[0] || null}
          >
            <Upload
              accept="image/*"
              className="custom-upload"
              listType="picture-card"
              maxCount={1}
              fileList={coverFileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setCoverFileList(fileList)}
              showUploadList={{ showPreviewIcon: false }}
            >
              {coverFileList.length >= 1 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default Bots
